import { CanActivateFn, Router } from '@angular/router';
import { inject } from '@angular/core';
import {
  ApiService,
  PermissionHelperService,
  PermissionsService,
  ProfileService,
} from '@app-utils';
import {
  catchError,
  combineLatest,
  filter,
  finalize,
  map,
  of,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { AppLoaderService } from '../services';

export const organizationGuard: CanActivateFn = (route, state) => {
  const router = inject(Router);
  const apiSvc = inject(ApiService);
  const loaderSvc = inject(AppLoaderService);
  const profileSvc = inject(ProfileService);
  const permissionHelperSvc = inject(PermissionHelperService);
  const permissionSvc = inject(PermissionsService);

  loaderSvc.setOrganizationLoader(true);
  const orgId = route.paramMap.get('orgId');
  const orgIdMatches = (id: string | undefined | null) =>
    !!orgId && id != null && String(id) === String(orgId);

  return combineLatest([
    profileSvc.organizationList$,
    profileSvc.profile$,
  ]).pipe(
    filter(([orgRes, profile]) => {
      if (!profile) return false;
      if (!orgId) return true;
      if (orgRes.some((item) => orgIdMatches(item.id))) return true;
      const expectedOrgCount = profile.organisation?.length ?? 0;
      if (expectedOrgCount === 0) return true;
      return orgRes.length >= expectedOrgCount;
    }),
    take(1),
    tap(([orgRes]) => {
      const org = orgRes.find((item) => orgIdMatches(item.id));
      if (!org) {
        void router.navigate(['/']);
        return;
      }
      profileSvc.setOrganization(org);
    }),
    map(([orgRes]) => orgRes.some((item) => orgIdMatches(item.id))),
    switchMap((res) => {
      const ltsOrgId = profileSvc.latestOrganization()?.id;
      const userId = profileSvc.latestProfile?.id;
      const isAdmin = permissionSvc.isSuperAdmin || permissionSvc.isAdmin;

      const setOrgCatalogFromApi = (
        apiPermissions: Parameters<
          PermissionHelperService['getMappedPermissionsBasedOnDataFromAPI']
        >[0],
      ) => {
        const mapped = permissionHelperSvc.getMappedPermissionsBasedOnDataFromAPI(
          apiPermissions,
          { forGrantUi: true },
        );
        profileSvc.setOrgModulePermissions(mapped);
      };

      const finishLoader = () => loaderSvc.setOrganizationLoader(false);

      if (ltsOrgId && isAdmin) {
        return apiSvc.modulePermissions().pipe(
          map((permissions) => {
            finishLoader();
            setOrgCatalogFromApi(permissions.data);
            return { org: res };
          }),
        );
      }

      if (ltsOrgId && permissionSvc.isMember && userId) {
        return apiSvc.getUserPermissionOfOrg('', userId).pipe(
          map((userPerm) => {
            const userDetails = userPerm.data?.userDetails || [];
            profileSvc.setMemberModulePermissions(userDetails);
            profileSvc.updateOrganization({ modulePermissions: userDetails });
            setOrgCatalogFromApi(
              profileSvc.latestOrganization()?.modulePermissions || [],
            );
            finishLoader();
            return { org: res };
          }),
          catchError(() => {
            const org = profileSvc.latestOrganization();
            setOrgCatalogFromApi(org?.modulePermissions || []);
            finishLoader();
            return of({ org: res });
          }),
        );
      }

      return of({ org: res }).pipe(
        map((data) => {
          const org = profileSvc.latestOrganization();
          setOrgCatalogFromApi(org?.modulePermissions || []);
          finishLoader();
          return data;
        }),
      );
    }),
    map((res) => {
      const memberDetails = profileSvc.latestMemberModulePermissions();
      const permissionSource = memberDetails?.length
        ? memberDetails
        : profileSvc.latestOrganization()?.modulePermissions || [];
      const userPermissionAccess =
        permissionHelperSvc.getUserPermissionAccess(permissionSource);
      profileSvc.setUserPermission(userPermissionAccess);
      return res;
    }),
    map((res) => !!res.org),
    finalize(() => loaderSvc.setOrganizationLoader(false)),
  );
};
